import React from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import DynamicModule from './DynamicModule';
import { useHelpKeyOverride } from '../../context/HelpContext';

/**
 * DynamicModulePage - Route component for config-driven modules. The navigation resolver renders it
 * for an Engineering row: navKey names the row (writes send it as X-Nav-Key), tableName is the
 * row's table, navSlug its URL slug.
 *
 * URL structure:
 * - /buildings           → First record, panel collapsed
 * - /buildings/123       → Record 123, panel collapsed
 * - /buildings/123?panel=open  → Record 123, panel expanded
 * - /buildings?search=FAB      → Search for FAB
 *
 * DynamicModule uses the dynamic API which auto-resolves FK display values.
 */

const DynamicModulePage = ({ navKey, tableName, navSlug }) => {
  const { recordId } = useParams();
  const [searchParams] = useSearchParams();

  // Footer help popup resolves this module's help row instead of the route slug
  useHelpKeyOverride(`module:${tableName}`);

  // Extract URL state
  const urlRecordId = recordId ? parseInt(recordId, 10) : null;
  const urlSearch = searchParams.get('search') || '';
  const urlPanelOpen = searchParams.get('panel') === 'open';
  const urlRelatedCollapsed = searchParams.get('related') === 'collapsed';
  // Datasheet view + its column filters are shareable (DevTrack 114). 'list' is the
  // default and is left out of the URL. Filters stay as the raw string here so the
  // prop identity is stable — parsing to an object every render would retrigger
  // DynamicModule's reset effect forever.
  const urlView = searchParams.get('view') === 'datasheet' ? 'datasheet' : 'list';
  const urlFiltersRaw = searchParams.get('filters') || '';
  const urlSortRaw = searchParams.get('sort') || '';

  return (
    <DynamicModule
      tableName={tableName}
      navKey={navKey}
      navSlug={navSlug}
      urlRecordId={urlRecordId}
      urlSearch={urlSearch}
      urlPanelOpen={urlPanelOpen}
      urlRelatedCollapsed={urlRelatedCollapsed}
      urlView={urlView}
      urlFiltersRaw={urlFiltersRaw}
      urlSortRaw={urlSortRaw}
    />
  );
};

export default DynamicModulePage;
